/**
 * Step 3: Interview Rounds Component
 *
 * Enhanced job post interview rounds form with:
 * - Auto-save via React Hook Form
 * - AI-generated data pre-fill
 * - Drag-and-drop reordering
 * - Default and custom rounds
 * - Real-time validation
 * - Progress tracking
 * - Card layout with scrollable content
 *
 * UI identical to V2, form management uses React Hook Form
 */

import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Card,
  Alert,
  Typography,
  message,
  Skeleton,
  Button,
  Space,
  InputNumber,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useFormContext } from "react-hook-form";
import { JobPostFormData } from "../../schemas/jobPostValidation";
import {
  calculateStep3CompletionPercentage,
  validateStep3Complete,
} from "../../hooks/useFormValidation";
import InterviewRoundsList from "./components/InterviewRoundsList";
import CustomRoundModal from "./components/CustomRoundModal";
// import CustomiseRoundModal from './components/CustomiseRoundModal'; // DEPRECATED: Settings now in CustomRoundModal
import SampleQuestionsModal from "./components/SampleQuestionsModal";
import {
  InterviewRoundState,
  transformStoreToStep3,
  transformFormToStep3,
} from "./utils/formTransformers";
import {
  ensureDefaultRounds,
  OVERALL_ASSESSMENT_DEFAULT_PROMPT,
} from "./utils/defaultRounds";
import { InterviewRound } from "../../../../Dto/GenerateJobDescription.dto";
import interviewService from "../../../../Service/interview.service";
import { Requirements } from "../../../../Entities/JobDescription";
import StepFooter from "../../components/StepFooter";
import { PersistenceStatus } from "../../hooks/useFormPersistence";
import { DEFAULT_PASS_PERCENTAGE } from "../../utils/constants";
import "./index.scss";

interface Step3InterviewRoundsProps {
  currentStep: number;
  totalSteps: number;
  prompt: string;
  isGenerating: boolean;
  onPromptChange: (value: string) => void;
  onGenerate: () => void;
  onPrevious: () => void;
  onNext: () => void;
  isPreviousDisabled?: boolean;
  isNextDisabled?: boolean;
  stepTitles: string[];
  isNavigating?: boolean;
  saveStatus: PersistenceStatus;
}

const Step3InterviewRounds: React.FC<Step3InterviewRoundsProps> = ({
  currentStep,
  totalSteps,
  prompt,
  isGenerating,
  onPromptChange,
  onGenerate,
  onPrevious,
  onNext,
  isPreviousDisabled = false,
  isNextDisabled = false,
  stepTitles,
  isNavigating = false,
  saveStatus,
}) => {
  const { control, watch, setValue } = useFormContext<JobPostFormData>();

  // Watch form values
  const step3Data = watch("step3");
  const step2Data = watch("step2");
  const mode = watch("mode") as "new" | "edit" | "clone";
  const isEditMode = mode === "edit";
  const jobPostId = watch("jobPostId") as string | undefined;
  const isProfileMatchRequired = watch("step3.isProfileMatchRequired");
  const interviewRoundExpiry = watch("step3.interviewRoundExpiry") ?? 5;
  const [isInitialized, setIsInitialized] = useState(false);
  // Pre-emptively show loading skeleton if any rounds are missing title metadata.
  // Applies to all modes (edit, clone, new) to prevent "Untitled Round" flash
  // before async enrichment kicks in on first render.
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(() => {
    return (step3Data?.interviewRounds || []).some(
      (r: any) => !r._displayMetadata?.title && r.interviewRoundId !== 'profile-match'
    );
  });

  // Track original round IDs when loading in edit mode (to distinguish existing vs newly added rounds)
  const [originalRoundIds, setOriginalRoundIds] = useState<Set<string>>(
    new Set(),
  );

  // Constants for metadata freshness
  const METADATA_STALE_THRESHOLD = 5 * 60 * 1000; // 5 minutes (matches service cache TTL)

  // Check if metadata is stale
  const isMetadataStale = useCallback((round: InterviewRoundState): boolean => {
    // If no title or description, it's stale (need to fetch metadata)
    // For custom rounds, we need at least title; for default rounds, we need both
    if (!round.title) {
      return true;
    }

    // If we have an interviewRoundId but no description/questionGenerationPrompt, it's stale
    // This happens when loading from edit mode - rounds have IDs but no metadata
    if (
      round.interviewRoundId &&
      !round.description &&
      !round.questionGenerationPrompt
    ) {
      return true;
    }

    // Check if metadata is too old (older than service cache TTL)
    // We'll re-fetch if metadata is older than 5 minutes to ensure freshness
    if (round._fetchedAt) {
      const metadataAge = Date.now() - round._fetchedAt;
      return metadataAge > METADATA_STALE_THRESHOLD;
    }

    // If no _fetchedAt timestamp, consider it stale (needs initial fetch)
    return true;
  }, []);

  // Smart enrichment: only fetch if stale or missing
  const enrichRoundsWithMetadata = useCallback(
    async (
      roundsToEnrich: InterviewRoundState[],
      forceRefresh: boolean = false,
      skipSave: boolean = false,
    ): Promise<InterviewRoundState[]> => {
      const roundsNeedingEnrichment = roundsToEnrich.filter(
        (r) => forceRefresh || isMetadataStale(r),
      );

      if (roundsNeedingEnrichment.length === 0) {
        return roundsToEnrich; // All metadata is fresh
      }

      // Pre-fetch round metadata before parallel enrichment.
      // When a jobPostId is available (edit or clone mode), use getAllInterviewRoundsSpecificToJob
      // because its documents have _ids that directly match the interviewRoundId values stored
      // in the job post. For new mode (no jobPostId), fall back to org-level round templates.
      let allOrgRounds: InterviewRound[] = [];
      try {
        if (jobPostId) {
          allOrgRounds = await interviewService.getAllInterviewRoundsSpecificToJob(jobPostId);
        } else {
          allOrgRounds = await interviewService.getAllInterviewRoundsSpecificToOrganization(forceRefresh);
        }
      } catch (error) {
        console.error("Error pre-fetching rounds for enrichment:", error);
        // Fall back to org-level rounds if job-specific fetch fails
        try {
          allOrgRounds = await interviewService.getAllInterviewRoundsSpecificToOrganization(forceRefresh);
        } catch {
          // ignore
        }
      }

      const enrichedRounds = await Promise.all(
        roundsToEnrich.map(async (round) => {
          // Skip if not stale and not forcing refresh
          if (!forceRefresh && !isMetadataStale(round)) {
            return round;
          }

          // Try service cache first (fastest)
          // Use _id if available (for custom rounds loaded from edit mode), otherwise use interviewRoundId
          const roundIdToFetch = round._id || round.interviewRoundId;

          if (roundIdToFetch) {
            try {
              // For default rounds (profile-match, overall-assessment), look up by name
              // from all org rounds. We use getAllInterviewRoundsSpecificToOrganization
              // because getGenericRoundsOnly relies on the isGeneric flag which is stripped
              // during normalization, making it unreliable.
              if (
                roundIdToFetch === "profile-match" ||
                roundIdToFetch === "overall-assessment" ||
                round.interviewRoundId === "profile-match" ||
                round.interviewRoundId === "overall-assessment"
              ) {
                const allOrgRoundsForDefault =
                  await interviewService.getAllInterviewRoundsSpecificToOrganization(forceRefresh);
                const isOverallAssessment =
                  roundIdToFetch === "overall-assessment" ||
                  round.interviewRoundId === "overall-assessment";
                const apiRound = allOrgRoundsForDefault.find((r: any) => {
                  const nameNorm = (r.title || r.roundName || "")
                    .toLowerCase()
                    .replace(/\s+/g, "");
                  if (isOverallAssessment) {
                    return nameNorm === "overallassessment";
                  } else {
                    // Profile Matching — match by name or category
                    const categories = r.category || [];
                    const hasProfileMatchCategory = categories.some(
                      (cat: string) =>
                        cat.toLowerCase().replace(/\s+/g, "") === "profilematch",
                    );
                    return (
                      nameNorm === "profilematching" || hasProfileMatchCategory
                    );
                  }
                });

                if (apiRound) {
                  const apiRoundAny = apiRound as any;
                  return {
                    ...round,
                    title:
                      apiRoundAny.roundName ||
                      apiRound.title ||
                      round.title ||
                      "",
                    description:
                      apiRound.description || round.description || "",
                    questionGenerationPrompt:
                      apiRoundAny.customRound?.custom_ai_prompt ||
                      apiRound.questionGenerationPrompt ||
                      round.questionGenerationPrompt ||
                      "",
                    evaluationPrompt:
                      apiRoundAny.customRound?.evaluation_criteria ||
                      apiRound.evaluationPrompt ||
                      round.evaluationPrompt ||
                      "",
                    answerType:
                      apiRoundAny.customRound?.question_type ||
                      apiRound.answerType ||
                      round.answerType ||
                      "mcq",
                    category:
                      apiRound.category ||
                      apiRoundAny.category ||
                      round.category,
                    isMustQualify: round.isMustQualify ?? true, // Preserve existing value
                    passPercentage:
                      round.passPercentage ?? DEFAULT_PASS_PERCENTAGE, // Preserve existing value
                    _fetchedAt: Date.now(),
                    _version: (round._version || 0) + 1,
                    _isNewlyAdded: round._isNewlyAdded, // Preserve newly added flag
                  };
                }
              } else {
                // For custom rounds, first look up in the pre-fetched bulk org rounds list.
                // This is more reliable than the individual /round/:id endpoint and
                // avoids extra API calls since allOrgRounds is already cached.
                const bulkRoundId = round._id || round.interviewRoundId;
                let fullRound: any = bulkRoundId
                  ? (allOrgRounds.find((r) => r._id === bulkRoundId) ?? null)
                  : null;

                // Fall back to individual getInterviewRoundById only if not found in bulk list
                if (!fullRound) {
                  if (round._id) {
                    fullRound = await interviewService.getInterviewRoundById(
                      round._id,
                      forceRefresh,
                    );
                  }
                  if (!fullRound && round.interviewRoundId) {
                    fullRound = await interviewService.getInterviewRoundById(
                      round.interviewRoundId,
                      forceRefresh,
                    );
                  }
                }

                if (fullRound) {
                  const apiRound = fullRound as any;
                  return {
                    ...round,
                    title:
                      apiRound.roundName ||
                      fullRound.title ||
                      round.title ||
                      "",
                    description:
                      fullRound.description || round.description || "",
                    questionGenerationPrompt:
                      apiRound.customRound?.custom_ai_prompt ||
                      fullRound.questionGenerationPrompt ||
                      round.questionGenerationPrompt ||
                      "",
                    evaluationPrompt:
                      apiRound.customRound?.evaluation_criteria ||
                      fullRound.evaluationPrompt ||
                      round.evaluationPrompt ||
                      "",
                    answerType:
                      apiRound.customRound?.question_type ||
                      fullRound.answerType ||
                      round.answerType ||
                      "mcq",
                    category:
                      fullRound.category || apiRound.category || round.category,
                    isMustQualify: round.isMustQualify ?? true, // Preserve existing value
                    passPercentage:
                      round.passPercentage ?? DEFAULT_PASS_PERCENTAGE, // Preserve existing value
                    _id: fullRound._id || round._id,
                    interviewRoundId: fullRound._id || round.interviewRoundId,
                    _fetchedAt: Date.now(),
                    _version: (round._version || 0) + 1,
                    _isNewlyAdded: round._isNewlyAdded, // Preserve newly added flag
                  };
                }
              }
            } catch (error) {
              console.error("Error fetching round metadata:", error);
              // Return existing metadata if fetch fails (graceful degradation)
              return round;
            }
          }

          return round;
        }),
      );

      // Save enriched rounds back to form (unless skipSave is true)
      // Only update interviewRounds, not isProfileMatchRequired or interviewRoundExpiry,
      // to avoid overwriting user changes made while the async fetch was in progress.
      if (!skipSave) {
        const step3StoreData = transformFormToStep3(
          enrichedRounds,
          isProfileMatchRequired ?? true,
          interviewRoundExpiry,
        );
        setValue("step3.interviewRounds", step3StoreData.interviewRounds, { shouldDirty: false });
      }

      return enrichedRounds;
    },
    [setValue, isMetadataStale, isProfileMatchRequired, interviewRoundExpiry, jobPostId],
  );

  // ensureDefaultRounds is now imported from shared utility

  // Convert form data to InterviewRoundState for local management
  const rounds = useMemo(() => {
    if (!step3Data || !step3Data.interviewRounds) {
      return [];
    }
    const transformed = transformStoreToStep3(step3Data);
    // In edit mode, only show existing rounds - don't auto-add defaults
    // In new mode, ensure default rounds exist (don't auto-add Overall Assessment when transforming existing data)
    return ensureDefaultRounds(transformed, false, isEditMode);
  }, [step3Data, ensureDefaultRounds, isEditMode]);

  // Initialize default rounds on component mount if empty (in create mode: new/clone)
  // This ensures rounds are initialized even if user navigates directly to review step
  // Also track original round IDs in edit mode
  useEffect(() => {
    // In create mode (new/clone), initialize default rounds if empty
    if (
      !isInitialized &&
      !isEditMode &&
      (!step3Data ||
        !step3Data.interviewRounds ||
        step3Data.interviewRounds.length === 0)
    ) {
      // Auto-add Overall Assessment only during initial setup (new job post)
      const defaultRounds = ensureDefaultRounds([], true, false);
      const step3StoreData = transformFormToStep3(
        defaultRounds,
        isProfileMatchRequired ?? true,
      );
      setValue("step3", step3StoreData, { shouldDirty: false });
      setIsInitialized(true);
    } else if (!isInitialized) {
      // In edit mode, track original round IDs to distinguish existing vs newly added rounds
      if (isEditMode && step3Data?.interviewRounds) {
        const originalIds = new Set<string>();
        step3Data.interviewRounds.forEach((round) => {
          const id =
            round.interviewRoundId || (round as any)._displayMetadata?._id;
          if (id) {
            originalIds.add(id);
          }
        });
        setOriginalRoundIds(originalIds);
      }
      setIsInitialized(true);
    }
  }, [step3Data, isInitialized, isEditMode, ensureDefaultRounds, setValue]);

  // Enrich rounds with metadata on mount and when rounds change
  useEffect(() => {
    const loadMetadata = async () => {
      if (rounds.length > 0 && isInitialized) {
        const needsEnrichment = rounds.some((r) => isMetadataStale(r));
        if (needsEnrichment) {
          setIsLoadingMetadata(true);
          try {
            // Force refresh if rounds don't have title/description (loaded from edit mode)
            const hasMissingMetadata = rounds.some(
              (r) =>
                !r.title ||
                (r.interviewRoundId &&
                  !r.description &&
                  !r.questionGenerationPrompt),
            );
            const enrichedRounds = await enrichRoundsWithMetadata(
              rounds,
              hasMissingMetadata,
            );

            // Verify enrichment worked - if any rounds still don't have title, log warning
            const stillMissingMetadata = enrichedRounds.some(
              (r) => r.interviewRoundId && !r.title,
            );
            if (stillMissingMetadata) {
              console.warn(
                "Some rounds still missing metadata after enrichment:",
                enrichedRounds.filter((r) => r.interviewRoundId && !r.title),
              );
            }
          } catch (error) {
            console.error("Error enriching rounds with metadata:", error);
          } finally {
            setIsLoadingMetadata(false);
          }
        } else {
          // No enrichment needed — ensure loading state is cleared
          // (handles case where isLoadingMetadata was pre-initialized to true)
          setIsLoadingMetadata(false);
        }
      }
    };
    loadMetadata();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rounds.length, isInitialized, step3Data?.interviewRounds?.length]); // Re-run when rounds change or step3Data changes

  // Calculate completion state for validation
  const isComplete = useMemo(() => {
    return validateStep3Complete(step3Data);
  }, [step3Data]);

  // Local UI state
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRound, setEditingRound] = useState<InterviewRound | null>(null);
  const [isSettingsOnlyMode, setIsSettingsOnlyMode] = useState(false);

  // Get requirements from Step2
  const requirements: Requirements[] = useMemo(() => {
    if (!step2Data || !step2Data.requirements) {
      return [];
    }
    return step2Data.requirements.map((req: any) => ({
      id: req.id || 0,
      topic: req.topic || "",
      description: req.description || "",
      mustMatch: req.mustMatch ?? true,
    }));
  }, [step2Data]);

  // Handle customise round - DEPRECATED: Settings now in CustomRoundModal
  // const handleCustomiseRound = (round: InterviewRoundState) => {
  //   setSelectedRoundForCustomise(round);
  //   setIsCustomiseModalVisible(true);
  // };

  // Handle save customise settings - Still used by CustomRoundModal via handleModalSubmit
  // This function is no longer called directly but the logic is now inline in handleModalSubmit
  // Keeping for reference
  // const handleSaveCustomise = (roundId: string, passPercentage: number, isMustQualify: boolean, isAIGenerated: boolean) => {
  //   const updatedRounds = rounds.map(round => {
  //     const id = round.interviewRoundId || round._id || '';
  //     if (id === roundId) {
  //       return {
  //         ...round,
  //         passPercentage,
  //         isMustQualify,
  //         // Note: isAIGenerated is always true for Step 3 rounds, but we store it for consistency
  //       };
  //     }
  //     return round;
  //   });
  //   handleRoundsChange(updatedRounds);
  // };

  // Handle close customise modal - DEPRECATED: Settings now in CustomRoundModal
  // const handleCloseCustomiseModal = () => {
  //   setIsCustomiseModalVisible(false);
  //   setSelectedRoundForCustomise(null);
  // };

  // Handle rounds change and save to form
  const handleRoundsChange = useCallback(
    (newRounds: InterviewRoundState[]) => {
      // In edit mode, preserve _isNewlyAdded flag for rounds that were newly added
      // Also ensure newly added rounds keep their flag when rounds are reordered or modified
      const roundsWithPreservedFlags = newRounds.map((round) => {
        const roundId = round.interviewRoundId || round._id || "";
        // If round doesn't have _isNewlyAdded flag but is not in original rounds, mark it as newly added
        if (
          isEditMode &&
          !round._isNewlyAdded &&
          roundId &&
          !originalRoundIds.has(roundId)
        ) {
          return { ...round, _isNewlyAdded: true };
        }
        return round;
      });

      // In edit mode, just save the rounds as-is (don't auto-add defaults)
      // In new mode, ensure default rounds exist before saving (don't auto-add Overall Assessment when user makes changes)
      const normalizedRounds = ensureDefaultRounds(
        roundsWithPreservedFlags,
        false,
        isEditMode,
      );
      const step3StoreData = transformFormToStep3(
        normalizedRounds,
        isProfileMatchRequired ?? true,
        interviewRoundExpiry,
      );
      setValue("step3", step3StoreData, { shouldDirty: true });
    },
    [
      setValue,
      ensureDefaultRounds,
      isEditMode,
      originalRoundIds,
      isProfileMatchRequired,
      interviewRoundExpiry,
    ],
  );

  const handleProfileMatchRequiredChange = (checked: boolean) => {
    // Per requirements: not modifiable during edit; default is true
    if (isEditMode) {
      return;
    }
    setValue("step3.isProfileMatchRequired", checked, { shouldDirty: true });
  };

  // Handle add round
  const handleAddRound = () => {
    setEditingRound(null);
    setIsSettingsOnlyMode(false);
    setIsModalVisible(true);
  };

  // Handle edit round - optimized for speed
  const handleEditRound = (round: InterviewRoundState) => {
    // In edit mode, only allow editing newly added rounds (not existing ones)
    if (isEditMode) {
      const roundId = round.interviewRoundId || round._id || "";
      const isNewlyAdded =
        round._isNewlyAdded || (roundId && !originalRoundIds.has(roundId));

      if (!isNewlyAdded) {
        message.warning(
          "Existing assessments cannot be edited when editing a job post. You can only edit assessments that you add during this edit session.",
        );
        return;
      }
    }

    // Check if this is a predefined round (non-editable)
    const isProfileMatching =
      round.title?.toLowerCase() === "profile matching" ||
      round.interviewRoundId === "profile-match" ||
      round._id === "profile-match";
    const isJobAlignment =
      round.title?.toLowerCase() === "job alignment" ||
      round.interviewRoundId === "job-alignment" ||
      round._id === "job-alignment";
    const isOverallAssessment =
      round.title?.toLowerCase() === "overall assessment" ||
      round.interviewRoundId === "overall-assessment" ||
      round._id === "overall-assessment";

    // Profile Matching and Job Alignment cannot be edited at all
    if (isProfileMatching || isJobAlignment) {
      message.warning(
        "Predefined rounds (Profile Matching, Job Alignment) cannot be edited",
      );
      return;
    }

    // Overall Assessment can be edited in settings-only mode (passPercentage and isMustQualify only)
    if (isOverallAssessment) {
      const roundForModal: InterviewRound = {
        _id: round._id || round.interviewRoundId || undefined,
        title: round.title || "Overall Assessment",
        description: round.description || "",
        questionGenerationPrompt:
          round.questionGenerationPrompt ||
          round.description ||
          OVERALL_ASSESSMENT_DEFAULT_PROMPT,
        evaluationPrompt: round.evaluationPrompt || "",
        answerType: round.answerType || "mixed",
      };
      setEditingRound(roundForModal);
      setIsModalVisible(true);
      setIsSettingsOnlyMode(true);
      return;
    }

    // Open modal immediately with existing round data (no async operations)
    const roundForModal: InterviewRound = {
      _id: round._id || round.interviewRoundId || undefined,
      title: round.title || "",
      description: round.description || "",
      questionGenerationPrompt: round.questionGenerationPrompt || "",
      evaluationPrompt: round.evaluationPrompt || "",
      answerType: round.answerType || "mcq",
    };

    setEditingRound(roundForModal);
    setIsSettingsOnlyMode(false);
    setIsModalVisible(true);

    // Fetch full details in background (non-blocking) with cancellation support
    if (round.interviewRoundId) {
      const fetchRoundId = round.interviewRoundId;
      const editRoundId = round._id;

      interviewService
        .getInterviewRoundById(fetchRoundId)
        .then((fullRound) => {
          if (fullRound) {
            const apiRound = fullRound as any;
            const enrichedRound: InterviewRound = {
              _id: fullRound._id,
              title: apiRound.roundName || fullRound.title || round.title || "",
              description: fullRound.description || round.description || "",
              questionGenerationPrompt:
                apiRound.customRound?.custom_ai_prompt ||
                fullRound.questionGenerationPrompt ||
                round.questionGenerationPrompt ||
                "",
              evaluationPrompt:
                apiRound.customRound?.evaluation_criteria ||
                fullRound.evaluationPrompt ||
                round.evaluationPrompt ||
                "",
              answerType:
                apiRound.customRound?.question_type ||
                fullRound.answerType ||
                round.answerType ||
                "mcq",
            };
            // Update modal only if it's still open and showing the same round
            // This prevents stale data from overwriting newer data if user quickly switches rounds
            setEditingRound((current) => {
              if (
                current &&
                (current._id === fetchRoundId || current._id === editRoundId)
              ) {
                return enrichedRound;
              }
              // Don't update if modal is closed or showing different round
              return current;
            });
          }
        })
        .catch((error) => {
          // Only log if we're still interested in this round
          // Avoids logging errors for cancelled/stale requests
          setEditingRound((current) => {
            if (
              current &&
              (current._id === fetchRoundId || current._id === editRoundId)
            ) {
              console.error("Error fetching round details:", error);
            }
            return current;
          });
        });
    }
  };

  // Handle delete round
  const handleDeleteRound = (roundId: string) => {
    // In edit mode, only allow deleting newly added rounds (not existing ones)
    if (isEditMode) {
      const roundToDelete = rounds.find(
        (round) => (round.interviewRoundId || round._id) === roundId,
      );

      if (roundToDelete) {
        const isNewlyAdded =
          roundToDelete._isNewlyAdded ||
          (roundId && !originalRoundIds.has(roundId));

        if (!isNewlyAdded) {
          message.warning(
            "Existing assessments cannot be deleted when editing a job post. You can only delete assessments that you add during this edit session.",
          );
          return;
        }
      }
    }

    const roundToDelete = rounds.find(
      (round) => (round.interviewRoundId || round._id) === roundId,
    );

    if (!roundToDelete) {
      return;
    }

    // Check if this is a predefined round
    const isProfileMatching =
      roundToDelete.title?.toLowerCase() === "profile matching" ||
      roundToDelete.interviewRoundId === "profile-match" ||
      roundToDelete._id === "profile-match";

    // Only allow deletion of Overall Assessment, not Profile Matching
    if (isProfileMatching) {
      message.warning("Profile Matching round cannot be deleted");
      return;
    }

    const newRounds = rounds.filter(
      (round) => (round.interviewRoundId || round._id) !== roundId,
    );
    handleRoundsChange(newRounds);
    message.success("Assessment deleted successfully");
  };

  // Handle toggle must-qualify
  const handleToggleMustQualify = (roundId: string, value: boolean) => {
    // Prevent toggling Profile Matching round (always must be true)
    const roundToToggle = rounds.find(
      (round) => (round.interviewRoundId || round._id) === roundId,
    );
    if (
      roundToToggle &&
      (roundToToggle.interviewRoundNumber === 1 ||
        roundToToggle.title?.toLowerCase() === "profile matching" ||
        roundToToggle.interviewRoundId === "profile-match")
    ) {
      message.warning(
        'Profile Matching round must always be set to "Must Qualify"',
      );
      return;
    }

    const newRounds = rounds.map((round) => {
      const id = round.interviewRoundId || round._id || "";
      return id === roundId ? { ...round, isMustQualify: value } : round;
    });
    handleRoundsChange(newRounds);
  };

  // Handle reorder (from drag and drop)
  const handleReorder = (newRounds: InterviewRoundState[]) => {
    // In edit mode, only allow reordering if no existing round's position changes
    // This allows newly added rounds to be reordered among themselves
    // while protecting existing rounds from position changes that could affect candidates in progress
    if (isEditMode) {
      // Build a map of existing rounds' original positions
      const existingRoundsOriginalPositions = new Map<string, number>();
      rounds.forEach((r) => {
        const id = r.interviewRoundId || r._id || "";
        if (id && !r._isNewlyAdded) {
          existingRoundsOriginalPositions.set(id, r.interviewRoundNumber);
        }
      });

      // Check if any existing round's position changed in the new order
      let existingRoundMoved = false;
      newRounds.forEach((r) => {
        const id = r.interviewRoundId || r._id || "";
        if (id && !r._isNewlyAdded) {
          const originalPosition = existingRoundsOriginalPositions.get(id);
          if (
            originalPosition !== undefined &&
            originalPosition !== r.interviewRoundNumber
          ) {
            existingRoundMoved = true;
          }
        }
      });

      if (existingRoundMoved) {
        message.warning(
          "You can only reorder newly added rounds among themselves. Existing rounds must maintain their positions to avoid affecting candidates in progress.",
        );
        return;
      }
    }
    handleRoundsChange(newRounds);
  };

  // Helper: Check if a round is a duplicate
  const isDuplicateRound = useCallback(
    (round: InterviewRound, excludeRoundId?: string | null): boolean => {
      if (!round._id) {
        return false; // Rounds without IDs can't be duplicates
      }

      return rounds.some((existingRound) => {
        // Skip the round being edited
        if (excludeRoundId) {
          const existingId =
            existingRound.interviewRoundId || existingRound._id;
          if (existingId === excludeRoundId) {
            return false;
          }
        }

        // Check if IDs match
        const existingId = existingRound.interviewRoundId || existingRound._id;
        return existingId && existingId === round._id;
      });
    },
    [rounds],
  );

  // Handle modal submit
  const handleModalSubmit = async (
    round: InterviewRound,
    passPercentage?: number,
    isMustQualify?: boolean,
  ) => {
    if (editingRound && editingRound._id) {
      // Update existing round
      const apiRound = round as any;
      const newRounds = rounds.map((r) => {
        const id = r.interviewRoundId || r._id || "";
        if (id === editingRound._id) {
          return {
            ...r,
            title: apiRound.roundName || round.title || r.title,
            description: round.description || r.description,
            questionGenerationPrompt:
              round.questionGenerationPrompt || r.questionGenerationPrompt,
            evaluationPrompt: round.evaluationPrompt || r.evaluationPrompt,
            answerType:
              apiRound.customRound?.question_type ||
              round.answerType ||
              r.answerType ||
              "mcq",
            category: round.category || r.category,
            // Use passed values from modal form, fallback to existing values
            isMustQualify: isMustQualify ?? r.isMustQualify ?? true,
            passPercentage:
              passPercentage ?? r.passPercentage ?? DEFAULT_PASS_PERCENTAGE,
            _id: round._id || r._id,
            interviewRoundId: round._id || r.interviewRoundId,
            _fetchedAt: Date.now(), // Update fetch timestamp
            _version: (r._version || 0) + 1, // Increment version
            _isNewlyAdded: r._isNewlyAdded, // Preserve newly added flag
          };
        }
        return r;
      });
      handleRoundsChange(newRounds);
      message.success("Assessment updated successfully");
    } else {
      // Add new round - check for duplicates
      if (isDuplicateRound(round)) {
        message.error(
          "This assessment has already been added. Please select a different assessment template.",
        );
        return; // Don't close modal, allow user to select a different assessment template
      }

      const apiRound = round as any;
      const maxRoundNumber =
        rounds.length > 0
          ? Math.max(...rounds.map((r) => r.interviewRoundNumber))
          : 1;
      const nextRoundNumber = maxRoundNumber + 1;

      // Check if this is a template used as-is (not modified) or a newly created round
      // If round._id matches a template ID from available templates, it's a template reference
      // Otherwise, it's a newly created round
      const newRound: InterviewRoundState = {
        interviewRoundId: round._id || null, // This will be the template ID if used as-is, or new round ID if created
        interviewRoundNumber: nextRoundNumber,
        // Use passed values from modal form
        isMustQualify: isMustQualify ?? true,
        passPercentage: passPercentage ?? DEFAULT_PASS_PERCENTAGE,
        title: apiRound.roundName || round.title || "",
        description: round.description || "",
        questionGenerationPrompt: round.questionGenerationPrompt || "",
        evaluationPrompt: round.evaluationPrompt || "",
        answerType:
          apiRound.customRound?.question_type || round.answerType || "mcq",
        category: round.category,
        _id: round._id, // Template ID if used as-is, or new round ID if created
        _fetchedAt: Date.now(), // Set fetch timestamp
        _version: 1, // Initial version
        _isNewlyAdded: isEditMode, // Mark as newly added if in edit mode
      };
      const newRounds = [...rounds, newRound];
      handleRoundsChange(newRounds);
      message.success("Assessment added successfully");
    }
    setIsModalVisible(false);
    setEditingRound(null);
    setIsSettingsOnlyMode(false);
  };

  // Check if Overall Assessment is missing
  const hasOverallAssessment = useMemo(() => {
    return rounds.some(
      (round) =>
        round.title?.toLowerCase() === "overall assessment" ||
        round.interviewRoundId === "overall-assessment" ||
        round._id === "overall-assessment",
    );
  }, [rounds]);

  // Handle restore Overall Assessment
  const handleRestoreOverallAssessment = () => {
    // Add at the end
    let insertIndex = rounds.length;

    const newRound: InterviewRoundState = {
      interviewRoundId: "overall-assessment",
      interviewRoundNumber: insertIndex + 1,
      isMustQualify: true,
      passPercentage: DEFAULT_PASS_PERCENTAGE,
      title: "Overall Assessment",
      description: OVERALL_ASSESSMENT_DEFAULT_PROMPT,
      questionGenerationPrompt: OVERALL_ASSESSMENT_DEFAULT_PROMPT,
      evaluationPrompt: "",
      answerType: "mixed",
    };

    const newRounds = [
      ...rounds.slice(0, insertIndex),
      newRound,
      ...rounds
        .slice(insertIndex)
        .map((r) => ({
          ...r,
          interviewRoundNumber: r.interviewRoundNumber + 1,
        })),
    ];
    handleRoundsChange(newRounds);
    message.success("Overall Assessment round restored");
  };

  return (
    <div className="step3-container">
      <Card
        className="step3-card"
        title={
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                marginBottom: 4,
              }}
            >
              <Typography.Title
                level={5}
                style={{ marginTop: 0, marginBottom: 0, fontWeight: 600 }}
              >
                Interview Rounds
              </Typography.Title>
              <Space>
                {!hasOverallAssessment && (
                  <Button
                    type="text"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={handleRestoreOverallAssessment}
                  >
                    Add Overall Assessment
                  </Button>
                )}
              </Space>
            </div>
            <Typography.Text type="secondary" style={{fontWeight: 400, maxWidth: '600px', textWrap: 'wrap'}}>
              An interview round defines how candidates are assessed. The
              default Overall Assessment covers all job requirements
              add more rounds to customize your process.
            </Typography.Text>
          </div>
        }
      >
        {/* Scrollable Card Body */}
        <div className="step3-card-body">
          {/* Interview Round Expiry — common setting for all rounds */}
          <div style={{ marginBottom: 16 }}>
            <Typography.Title
              level={5}
              style={{ marginTop: 0, marginBottom: 0, fontWeight: 600 }}
            >
              Interview Round Expiry
            </Typography.Title>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Candidates must complete each round within the generated date plus
              the specified expiry days.
            </Typography.Text>
            <InputNumber
              min={1}
              precision={0}
              controls
              value={interviewRoundExpiry}
              disabled={isEditMode}
              onChange={(value) =>
                setValue("step3.interviewRoundExpiry", value ?? 5, {
                  shouldDirty: true,
                })
              }
              addonAfter={<span style={{ userSelect: "none" }}>days</span>}
              style={{ width: 140 }}
            />
            {isEditMode && (
              <Typography.Text
                type="secondary"
                style={{ fontSize: 12, display: "block", marginTop: 4 }}
              >
                Round expiry cannot be changed while editing a job post.
              </Typography.Text>
            )}
          </div>

          {/* Show validation warnings if needed */}
          {rounds.length > 0 && !isComplete && (
            <Alert
              message="At least one assessment is required"
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Show duplicate rounds warning */}
          {(() => {
            const duplicateRounds = rounds.filter((round, index) => {
              if (!round._id && !round.interviewRoundId) return false;
              const roundId = round.interviewRoundId || round._id;
              return rounds.some((otherRound, otherIndex) => {
                if (index === otherIndex) return false;
                const otherRoundId =
                  otherRound.interviewRoundId || otherRound._id;
                return roundId && otherRoundId && roundId === otherRoundId;
              });
            });

            if (duplicateRounds.length > 0) {
              return (
                <Alert
                  message={`Duplicate assessments detected. Please remove duplicate assessments: ${duplicateRounds.map((r) => r.title || "Untitled").join(", ")}`}
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              );
            }
            return null;
          })()}

          {/* Interview Rounds List */}
          {isLoadingMetadata ? (
            <Card>
              <Skeleton active paragraph={{ rows: 3 }} />
            </Card>
          ) : (
            <InterviewRoundsList
              rounds={rounds}
              onReorder={handleReorder}
              onEdit={handleEditRound}
              onDelete={handleDeleteRound}
              onAddRound={handleAddRound}
              isEditMode={isEditMode}
              isProfileMatchRequired={isProfileMatchRequired ?? true}
              onProfileMatchRequiredChange={handleProfileMatchRequiredChange}
            />
          )}
        </div>
        <div className="step3-card-footer">
          <StepFooter
            currentStep={currentStep}
            totalSteps={totalSteps}
            prompt={prompt}
            isGenerating={isGenerating}
            onPromptChange={onPromptChange}
            onGenerate={onGenerate}
            onPrevious={onPrevious}
            onNext={onNext}
            isPreviousDisabled={isPreviousDisabled}
            isNextDisabled={isNextDisabled}
            stepTitles={stepTitles}
            isNavigating={isNavigating}
            saveStatus={saveStatus}
          />
        </div>
      </Card>

      {/* Custom Round Modal */}
      <CustomRoundModal
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingRound(null);
          setIsSettingsOnlyMode(false);
        }}
        onSubmit={handleModalSubmit}
        editingRound={editingRound}
        existingRounds={rounds}
        // Pass current round settings for editing
        initialPassPercentage={
          editingRound
            ? rounds.find(
                (r) => (r.interviewRoundId || r._id) === editingRound._id,
              )?.passPercentage
            : undefined
        }
        initialIsMustQualify={
          editingRound
            ? rounds.find(
                (r) => (r.interviewRoundId || r._id) === editingRound._id,
              )?.isMustQualify
            : undefined
        }
        // Settings-only mode for predefined rounds like Overall Assessment
        settingsOnlyMode={isSettingsOnlyMode}
      />

      {/* Sample Questions Modal - Now integrated into CustomRoundModal */}

      {/* Customise Round Modal - DEPRECATED: Settings now in CustomRoundModal */}
      {/* <CustomiseRoundModal
        visible={isCustomiseModalVisible}
        round={selectedRoundForCustomise}
        onCancel={handleCloseCustomiseModal}
        onSave={handleSaveCustomise}
      /> */}
    </div>
  );
};

Step3InterviewRounds.displayName = "Step3InterviewRounds";

export default Step3InterviewRounds;
